import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import {
  AbacusTransferMessage,
  GroupedMessage,
  formatUtcTimestamp,
  groupMessages,
  toPlainText,
} from './abacus-message-grouping';

export { AbacusTransferMessage, GroupedMessage } from './abacus-message-grouping';

/**
 * Meldungen zu einem Beleg, kompakt dargestellt.
 *
 * Zwei Betriebsarten:
 *  - `messages`: Antworten von Abacus. Ein Beleg wird mehrfach übertragen, es
 *    gibt also mehrere. Sichtbar ist die NEUESTE, die übrigen lassen sich
 *    aufklappen.
 *  - `text`: ein einzelner Vermerk des Portals (etwa "wegen Fibu-Datum nicht
 *    gespeichert"). Bewusst getrennt gehalten, damit erkennbar bleibt, was von
 *    Abacus kommt und was wir selbst geschrieben haben.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'abacus-error-notice',
  templateUrl: './abacus-error-notice.component.html',
  styleUrls: ['./abacus-error-notice.component.css'],
})
export class AbacusErrorNoticeComponent implements OnChanges {
  @Input() text = '';
  @Input() changeDate = '';
  @Input() title = 'Abacus-Meldung';
  @Input() previewLength = 140;

  /** Antwortmeldungen von Abacus, neueste zuerst. */
  @Input() messages: AbacusTransferMessage[] = [];

  /**
   * 'error' = Abacus hat abgelehnt (rot). 'hint' = Erklärung des Portals,
   * warum der Beleg provisorisch ist (ruhiges Blau). Die Trennung ist wichtig:
   * Ein Hinweis ist kein Fehler und soll nicht so aussehen.
   */
  @Input() variant: 'error' | 'hint' = 'error';

  expanded = false;

  /** Bei Belegwechsel wieder einklappen, damit jeder Beleg mit der Vorschau startet. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text'] || changes['messages']) {
      this.expanded = false;
    }
  }

  get hasMessages(): boolean {
    return (this.messages || []).length > 0;
  }

  /** Anzahl der Übertragungsversuche insgesamt. */
  get messageCount(): number {
    return (this.messages || []).length;
  }

  /**
   * Gleiche Meldungen zusammengefasst, neueste zuerst.
   *
   * Nötig, weil ein Beleg bei wiederholten Übertragungsversuchen denselben
   * Fehler immer wieder zurückbekommt - am 11.09.2026 hatte ein Beleg 104
   * Meldungen, davon zwei verschiedene. Ungefiltert wäre das eine Wand aus
   * identischen Zeilen statt einer Information.
   */
  get grouped(): GroupedMessage[] {
    return groupMessages(this.messages || []);
  }

  /** Wie viele VERSCHIEDENE Meldungen es gibt. */
  get distinctCount(): number {
    return this.grouped.length;
  }

  /** Die neueste Meldung - die ist für den Benutzer die maßgebliche. */
  get newest(): AbacusTransferMessage | null {
    return this.hasMessages ? this.messages[0] : null;
  }

  /** Die übrigen VERSCHIEDENEN Meldungen, nur aufgeklappt sichtbar. */
  get older(): GroupedMessage[] {
    return this.grouped.slice(1);
  }

  /** Wiederholungen der neuesten Meldung, für den Zusatz "12 Versuche". */
  get newestRepeats(): number {
    const groups = this.grouped;
    return groups.length > 0 ? groups[0].count : 0;
  }

  /** Zeitpunkt der Kopfzeile: aus der neuesten Meldung, sonst der übergebene Stand. */
  get stand(): string {
    const newest = this.newest;
    return newest ? this.formatTimestamp(newest.fileTimestamp) : this.changeDate;
  }

  /** `yyyyMMddHHmmss` (UTC) zu `dd.MM.yyyy HH:mm` in Ortszeit. */
  formatTimestamp(value: string): string {
    return formatUtcTimestamp(value);
  }

  /** Reintext ohne HTML - für Vorschau und Längenprüfung. */
  toPlain(value: string): string {
    return toPlainText(value);
  }

  get plainText(): string {
    const newest = this.newest;
    return this.toPlain(newest ? newest.messageText : this.text);
  }

  get isLong(): boolean {
    return (
      this.plainText.length > this.previewLength ||
      this.older.length > 0 ||
      this.newestRepeats > 1
    );
  }

  get preview(): string {
    const t = this.plainText;
    return t.length > this.previewLength
      ? t.substring(0, this.previewLength).trim() + '…'
      : t;
  }

  get toggleLabel(): string {
    if (this.expanded) {
      return 'Weniger anzeigen';
    }
    return this.older.length > 0
      ? 'Alle ' + this.distinctCount + ' Meldungen anzeigen'
      : 'Komplette Meldung anzeigen';
  }

  get isHint(): boolean {
    return this.variant === 'hint';
  }

  get iconClass(): string {
    return this.isHint ? 'pi pi-info-circle' : 'pi pi-exclamation-triangle';
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }
}
